/* Share the collection picker, pricing model and real case catalogue with the guide pages. */
(() => {
  'use strict';
  const root = document.querySelector('[data-page-pc-builder]');
  if (!root || root.dataset.pageBuilderReady) return;
  root.dataset.pageBuilderReady = 'true';
  const page = root.closest('section');
  const link = root.querySelector('[data-page-builder-link]');
  const sidebarStatus = root.querySelector('[data-page-builder-status]');
  const results = page.querySelector('[data-page-pc-results]');
  const grid = page.querySelector('[data-page-results-grid]');
  const resultStatus = page.querySelector('[data-page-results-status]');
  const more = page.querySelector('[data-page-results-more]');
  const template = page.querySelector('[data-page-result-template]');
  const originalGrids = [...page.querySelectorAll('.epc-shop__grid, [data-best-grid]')];
  const browseLinks = new Map([...page.querySelectorAll('a[href]')]
    .filter(a => a !== link && /^\/collections\/(gaming-pcs|shop-by-processor)(?:[?#]|$)/.test(a.getAttribute('href')))
    .map(a => [a, a.getAttribute('href')]));
  const payload = JSON.parse(root.querySelector('[data-elite-collection-configurator-payload]').textContent);
  const storedPricing = JSON.parse(root.querySelector('[data-elite-collection-pc-pricing]').textContent);
  const bestConfiguration = page.querySelector('[data-best-configuration]');
  const bestDefaults = bestConfiguration ? JSON.parse(bestConfiguration.textContent).selections : null;
  const normalize = value => String(Array.isArray(value) ? value.join(' ') : value || '').replace(/&amp;/g,'&').toLowerCase().replace(/\bcase\b/g,'').replace(/[^a-z0-9]/g,'');
  let state = {selections:[], stockMode:'in_stock', pcPricing:storedPricing};
  let active = false, budget = '', signature = '', previousPricing, serial = 0, limit = 12, feedPromise;
  if (!root.hasAttribute('data-desktop-only-pc-builder') && matchMedia('(max-width: 990px)').matches) root.querySelector('details').open = false;

  function entries() {
    return [...root.querySelectorAll('[data-elite-configurator-filter-select]')].flatMap(select => {
      const option = select.selectedOptions[0];
      const value = option?.dataset.eliteParamValue || select.value;
      return value ? [{param:select.dataset.param, value, filterId:select.dataset.filterId, label:option?.textContent,
        ...(select.dataset.eliteImplicitNoneValue === value ? {isNone:true} : {})}] : [];
    });
  }
  function label(key, sku) {
    if (!sku) return '';
    const selected = state.selections.find(e => e.param === 'elite_' + key && e.value === sku);
    if (selected?.label) return selected.label.replace(/\s*\(\d+\)\s*$/,'');
    const filter = payload.filters?.find(f => f.componentKey === key);
    const option = filter?.options?.find(o => [o.sku,o.value,o.inStockSku,o.outOfStockSku,...(o.memberSkus || [])].includes(sku));
    return payload.componentLabels?.[key]?.[sku] || option?.label || '';
  }
  function caseMatches(product) {
    const cached = payload.cases?.find(c => c.sku === product.sku) || {};
    const available = product.available ?? cached.availability?.available;
    if (state.stockMode === 'in_stock' && available === false) return false;
    if (state.stockMode === 'out_of_stock' && available !== false) return false;
    return state.selections.every(entry => {
      if (entry.filterId === 'case_style' || entry.param === 'elite_case_style') return normalize(cached.style || product.style) === normalize(entry.value);
      if (['case','case_type','case_types'].includes(entry.filterId) || entry.param === 'elite_case_type') return normalize(cached.type || product.type) === normalize(entry.value);
      if (/^case_colou?r/.test(entry.filterId || '') || /case_colou?r/.test(entry.param || '')) return normalize(product.colour || cached.colour) === normalize(entry.value);
      return true;
    });
  }
  function selectionUrl() {
    const url = new URL('/collections/gaming-pcs',location.origin);
    if (root.dataset.pageCategory === 'nvidia') url.searchParams.set('gpu_family','nvidia-rtx');
    if (root.dataset.pageCategory === 'best') url.searchParams.set('sort_by','best-selling');
    state.selections.forEach(e => { if (e.param && e.value) url.searchParams.set(e.param,e.value); });
    if (['amd','intel'].includes(state.processorBrand)) url.searchParams.set('cpu_brand',state.processorBrand);
    else if (root.dataset.pageCategory === 'amd' && !state.selections.some(e => e.param === 'elite_cpu')) url.searchParams.set('cpu_brand','amd');
    if (state.stockMode === 'all') url.searchParams.set('availability','all');
    else url.searchParams.set('filter.v.availability',state.stockMode === 'out_of_stock' ? '0' : '1');
    if (budget) url.searchParams.set('budget',budget);
    return url;
  }
  async function loadFeed() {
    if (!feedPromise) feedPromise = (async () => {
      const products = [];
      let next = 1;
      while (next && next <= 20) {
        const url = new URL('/collections/gaming-pcs',location.origin);
        url.searchParams.set('section_id','elite-page-pc-feed');
        url.searchParams.set('page',String(next));
        if (root.dataset.pageCategory === 'best') url.searchParams.set('sort_by','best-selling');
        const response = await fetch(url,{signal:AbortSignal.timeout(25000)});
        if (!response.ok) throw new Error('Case catalogue unavailable');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const feed = JSON.parse(doc.querySelector('[data-page-pc-feed]')?.textContent || 'null');
        if (!Array.isArray(feed?.products)) throw new Error('Invalid case catalogue');
        products.push(...feed.products.filter(product => {
          const target = new URL(product.url,location.origin);
          return target.origin === location.origin && target.pathname.startsWith('/products/');
        }));
        if (feed.nextPage && feed.nextPage <= next) throw new Error('Invalid catalogue pagination');
        next = feed.nextPage;
      }
      return [...new Map(products.map(p => [p.sku,p])).values()];
    })().catch(error => { feedPromise = null; throw error; });
    return feedPromise;
  }
  function productLink(product, build) {
    const url = new URL(product.url,location.origin);
    url.searchParams.set('pc','gaming-pc');
    Object.entries(build.selections).forEach(([key,value]) => url.searchParams.set('elite_' + key,value));
    return url.pathname + url.search;
  }
  async function renderResults() {
    const run = ++serial;
    if (!results) return;
    results.hidden = !active;
    originalGrids.forEach(el => { el.hidden = active; });
    page.dataset.pageFiltersActive = String(active);
    if (!active) { grid.replaceChildren(); return; }
    grid.setAttribute('aria-busy','true');
    resultStatus.textContent = 'Updating matching PCs…';
    try {
      const products = await loadFeed();
      if (run !== serial || !active) return;
      const context = {processorBrand:state.processorBrand || (root.dataset.pageCategory === 'amd' ? 'amd' : ''),stockMode:state.stockMode};
      const pricing = bestDefaults ? {...state.pcPricing,defaultSelections:bestDefaults} : state.pcPricing;
      const matches = products.filter(caseMatches).map(product => ({product,build:window.EliteCollectionPCPrices.configuration(pricing,product.sku,state.selections,context)}))
        .filter(({build}) => build?.price && (!budget || budget.split(',').some(range => window.EliteCollectionPCPrices.budgetValues(build.price.pennies).includes(range))));
      const fragment = document.createDocumentFragment();
      for (const {product,build} of matches.slice(0,limit)) {
        const card = template.content.cloneNode(true);
        card.querySelector('[data-result-name]').textContent = product.title;
        const image = card.querySelector('[data-result-image]'); image.src = product.image; image.alt = product.title;
        card.querySelector('[data-result-stock]').textContent = product.available ? 'In stock' : 'Out of stock';
        card.querySelector('[data-result-price]').textContent = build.price.formatted;
        card.querySelectorAll('[data-result-link]').forEach(a => { a.href = productLink(product,build); });
        const specs = card.querySelector('[data-result-specs]');
        for (const [key,title] of [['cpu','Processor'],['gpu','Graphics'],['ram','Memory'],['psu','Power supply'],['nvme_ssd','NVMe SSD'],['sata_ssd','SATA SSD'],['sata_hdd','Hard drive']]) {
          const text = label(key,build.selections[key]);
          if (!text) continue;
          const row = document.createElement('div'), term = document.createElement('dt'), value = document.createElement('dd');
          term.textContent = title; value.textContent = text; row.append(term,value); specs.append(row);
        }
        fragment.append(card);
      }
      grid.replaceChildren(fragment);
      resultStatus.textContent = matches.length ? `${matches.length} matching gaming PC${matches.length === 1 ? '' : 's'} · showing ${Math.min(limit,matches.length)}` : 'No PCs match these filters. Change a selection or clear the filters.';
      more.hidden = matches.length <= limit;
      more.textContent = 'Show more PCs';
    } catch {
      if (run !== serial) return;
      grid.replaceChildren(); resultStatus.textContent = 'Couldn’t load matching PCs. Please try again.'; more.hidden = false; more.textContent = 'Try again';
    } finally { if (run === serial) grid.setAttribute('aria-busy','false'); }
  }
  function update(detail = {}) {
    state = {...state,...detail,selections:Array.isArray(detail.selections) ? detail.selections : entries(),pcPricing:detail.pcPricing || state.pcPricing};
    const next = JSON.stringify([state.selections.map(e=>[e.param,e.value]),state.stockMode,state.processorBrand,budget]);
    const changed = next !== signature || previousPricing !== state.pcPricing;
    signature = next; previousPricing = state.pcPricing;
    active = Boolean(state.selections.some(entry => !entry.isNone) || budget || ['amd','intel'].includes(state.processorBrand) || state.stockMode !== 'in_stock');
    const url = selectionUrl(); link.href = url.pathname + url.search;
    browseLinks.forEach((original,a) => { const browse = new URL(original,location.origin); url.searchParams.forEach((value,key) => { if (!browse.searchParams.has(key)) browse.searchParams.set(key,value); }); a.href = browse.pathname + browse.search; });
    sidebarStatus.textContent = active ? (root.hasAttribute('data-page-builder-browse-only') ? 'Your choices are ready — view matching PCs.' : 'Filters applied to this page') : '';
    if (!changed) return;
    limit = 12;
    renderResults();
    if (page.matches('[data-elite-bundles]')) document.dispatchEvent(new CustomEvent('elite:page-builder:filter',{detail:{...state,active,caseMatches,label,page}}));
  }
  document.addEventListener('elite:collection-configurator:change',event => { if (event.detail?.collectionHandle === 'gaming-pcs') update(event.detail); });
  root.addEventListener('click',event => { if (event.target.closest('[data-elite-filter-clear]')) { budget = ''; update(); } });
  page.querySelectorAll('.epc-shop__card').forEach(anchor => anchor.addEventListener('click',event => {
    const params = new URL(browseLinks.get(anchor),location.origin).searchParams;
    const component = params.has('elite_cpu') ? 'cpu' : params.has('elite_gpu') ? 'gpu' : '';
    if (component) {
      const select = root.querySelector(`[data-elite-configurator-filter-select][data-filter-id="${component}"]`);
      const requested = normalize(params.get('elite_' + component));
      const option = select && [...select.options].find(o => normalize(o.textContent).includes(requested) || normalize(o.value) === requested);
      if (!option) return;
      event.preventDefault(); select.value = option.value; select.dispatchEvent(new Event('change',{bubbles:true}));
    } else if (params.has('budget')) { event.preventDefault(); budget = params.get('budget'); update(state); }
  }));
  more?.addEventListener('click',() => { limit += 12; renderResults(); });
  update();
})();
